import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react"
import { ChevronRight, FilePenLine, FilePlus2, FileSearch, FileText, Search, SquareTerminal } from "lucide-react"
import { LoadingOrb } from "./LoadingOrb"

const STREAM_TEXT_MAX_LENGTH = 220
const STREAM_CODE_DUMP_HINT = /\b(CREATE|ALTER|INSERT|UPDATE|DELETE|SELECT|BEGIN|COMMIT|POLICY|TABLE|COLUMN|REFERENCES|DEFAULT|TIMESTAMP|UUID|NUMERIC|TEXT|BOOLEAN)\b|={8,}|-{6,}/i
const ACTIVITY_LABEL_MAX_LENGTH = 120
const ACTIVITY_STATUSES = [ "running", "completed", "failed" ]
// Runner/provider lifecycle plumbing is recorded for diagnostics but is not
// user-facing activity (Codex itself sends it to stderr); filter by raw type.
const PLUMBING_EVENT_RAW_TYPES = new Set([
  "provider_preflight_started",
  "provider_repository_snapshot_downloaded",
  "provider_credential_staged",
  "provider_command_started",
  "provider_output_started",
  "provider_command_exit",
  "codex.session_configured",
  "codex.turn_started",
  "codex.turn_completed",
  "codex.task_started",
  "codex.task_completed"
])
const defaultActivityFeedLines = [
  { active: false, key: "status", text: "Reading repository signals.", tone: "status" }
]
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect

export function activityFeedRows({ compact = true, events = [], lines = [] } = {}) {
  const eventRows = rowsFromEvents(events, { compact })
  if (eventRows.length > 0) return eventRows

  const sourceLines = lines.length > 0 ? lines : defaultActivityFeedLines
  return sourceLines.map((line, index) => ({
    active: Boolean(line.active),
    key: line.key || `line-${index}`,
    kind: "text",
    text: formatEventText(line.text, { compact }),
    tone: textRowTone(line.tone)
  })).filter((row) => row.text)
}

function rowsFromEvents(events, { compact }) {
  const rows = []
  const activityRowByItemId = new Map()

  ;(Array.isArray(events) ? events : []).forEach((event, index) => {
    if (!event) return

    const rawType = String(event.rawType ?? event.raw_type ?? "").toLowerCase()
    if (PLUMBING_EVENT_RAW_TYPES.has(rawType)) return

    const activity = normalizeActivity(event.activity)
    const source = event.agentRunId ? { agentRunId: event.agentRunId, commitSha: event.commitSha } : {}
    if (activity) {
      const activityKey = activity.itemId ? `${event.agentRunId || ""}:${activity.itemId}` : null
      const existingRow = activityKey ? activityRowByItemId.get(activityKey) : null
      if (existingRow) {
        existingRow.activity = { ...existingRow.activity, ...activity }
        return
      }

      const row = {
        ...source,
        activity,
        key: event.key || `${event.sequence || activity.label}-${event.type || "activity"}-${index}`,
        kind: "activity"
      }
      rows.push(row)
      if (activityKey) activityRowByItemId.set(activityKey, row)
      return
    }

    const text = formatEventText(event.text || event.raw_type || event.rawType || "", { compact })
    if (!text || looksLikeJsonPayload(text)) return

    rows.push({
      ...source,
      active: false,
      key: event.key || `${event.sequence || text}-${event.type || "status"}-${index}`,
      kind: "text",
      text,
      tone: textRowTone(event.type)
    })
  })

  return collapseRepeatedTextRows(rows)
}

function collapseRepeatedTextRows(rows) {
  return rows.reduce((collapsed, row) => {
    const previous = collapsed[collapsed.length - 1]
    if (previous && previous.kind === "text" && row.kind === "text" && previous.agentRunId === row.agentRunId && previous.text === row.text && previous.tone === row.tone) {
      previous.count = (previous.count || 1) + 1
      return collapsed
    }

    collapsed.push(row)
    return collapsed
  }, [])
}

// Structured result payloads (analysis cards, saved findings) are results,
// not activity; the page renders them in their own sections.
function looksLikeJsonPayload(text) {
  const trimmed = String(text || "").trim()
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return false

  try {
    const parsed = JSON.parse(trimmed)
    return parsed !== null && typeof parsed === "object"
  } catch {
    return false
  }
}

function textRowTone(type) {
  const tone = String(type || "status").toLowerCase()
  if ([ "done", "error", "reasoning", "status" ].includes(tone)) return tone
  return "status"
}

function normalizeActivity(value) {
  if (!value || typeof value !== "object") return null

  const label = clampActivityLabel(value.label)
  if (!label) return null

  return {
    changes: (Array.isArray(value.changes) ? value.changes : [])
      .filter((change) => change?.path)
      .map((change) => ({ kind: change.kind || "update", path: String(change.path) })),
    command: value.command ? String(value.command) : null,
    exitCode: Number.isInteger(value.exit_code) ? value.exit_code : null,
    itemId: value.item_id ? String(value.item_id) : null,
    label,
    output: value.output ? String(value.output) : null,
    status: ACTIVITY_STATUSES.includes(value.status) ? value.status : "completed",
    toolKind: value.tool_kind || "tool_use",
    toolName: value.tool_name || null
  }
}

function clampActivityLabel(label) {
  const compacted = String(label || "").replace(/\s+/g, " ").trim()
  if (compacted.length > ACTIVITY_LABEL_MAX_LENGTH) return `${compacted.slice(0, ACTIVITY_LABEL_MAX_LENGTH).trim()}...`
  return compacted
}

function activityHasDetail(activity) {
  return Boolean(activity.command || activity.output || activity.exitCode !== null || activity.changes.length > 0)
}

function activityIcon(activity) {
  if (activity.toolKind === "command_execution") return SquareTerminal
  if (activity.toolKind === "file_change") {
    return activity.changes.some((change) => change.kind === "add") ? FilePlus2 : FilePenLine
  }

  switch (activity.toolName) {
    case "Bash": return SquareTerminal
    case "Read": return FileSearch
    case "Edit":
    case "MultiEdit": return FilePenLine
    case "Write": return FileText
    case "Grep":
    case "Glob": return Search
    default: return SquareTerminal
  }
}

function changeKindLabel(kind) {
  if (kind === "add") return "Added"
  if (kind === "delete") return "Deleted"
  return "Updated"
}

export function AgentActivityFeed({ className = "", collapsible = false, events = [], generating = false, hidden = false, lines = [], orbState = null, transcript = false }) {
  const contentId = useId()
  const eventsRef = useRef(null)
  const followOutputRef = useRef(true)
  const shellRef = useRef(null)
  const streamRef = useRef(null)
  const [expanded, setExpanded] = useState(false)
  const [needsExpansion, setNeedsExpansion] = useState(false)
  const [openDetailKeys, setOpenDetailKeys] = useState(() => new Set())
  const rows = useMemo(() => activityFeedRows({ compact: !transcript, events, lines }), [events, lines, transcript])
  const collapsed = collapsible && (needsExpansion || generating) && !expanded
  const classNames = [
    "architecture-stream",
    className,
    collapsible ? "is-collapsible" : "",
    generating ? "is-generating" : "",
    transcript ? "is-transcript" : "",
    collapsed ? "is-collapsed" : "",
    expanded ? "is-expanded" : "",
    "agent-activity-feed"
  ].filter(Boolean).join(" ")

  useBrowserLayoutEffect(() => {
    if (!collapsible || hidden) {
      setNeedsExpansion(false)
      return undefined
    }

    const eventsElement = eventsRef.current
    const stream = streamRef.current
    if (!eventsElement || !stream) return undefined

    function collapsedContentHeight() {
      const value = window.getComputedStyle(stream).getPropertyValue("--architecture-stream-content-collapsed-h")
      return Number.parseFloat(value) || 138
    }

    function updateNeedsExpansion() {
      setNeedsExpansion(eventsElement.scrollHeight > collapsedContentHeight() + 1)
    }

    updateNeedsExpansion()

    const animationFrame = window.requestAnimationFrame?.(updateNeedsExpansion)
    const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(updateNeedsExpansion) : null
    resizeObserver?.observe(eventsElement)
    resizeObserver?.observe(stream)
    window.addEventListener("resize", updateNeedsExpansion)

    return () => {
      if (animationFrame) window.cancelAnimationFrame?.(animationFrame)
      resizeObserver?.disconnect()
      window.removeEventListener("resize", updateNeedsExpansion)
    }
  }, [collapsible, hidden, rows])

  useEffect(() => {
    if (generating) setExpanded(false)
  }, [generating])

  function handleScroll(event) {
    const shell = event.currentTarget
    const distanceFromBottom = shell.scrollHeight - shell.scrollTop - shell.clientHeight
    followOutputRef.current = distanceFromBottom <= 1
  }

  function toggleDetail(key) {
    setOpenDetailKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys)
      if (nextKeys.has(key)) {
        nextKeys.delete(key)
      } else {
        nextKeys.add(key)
      }
      return nextKeys
    })
  }

  useBrowserLayoutEffect(() => {
    if (hidden) return undefined

    const shell = shellRef.current
    if (!shell) return undefined

    function scrollToBottom() {
      if (!followOutputRef.current) return
      shell.scrollTop = shell.scrollHeight
    }

    scrollToBottom()
    const animationFrame = window.requestAnimationFrame?.(scrollToBottom)

    return () => {
      if (animationFrame) window.cancelAnimationFrame?.(animationFrame)
    }
  }, [collapsed, expanded, generating, hidden, rows, openDetailKeys])

  return (
    <div className={classNames} data-architecture-stream hidden={hidden} ref={streamRef} aria-busy={generating ? "true" : undefined}>
      {generating && orbState && !collapsed ? (
        <LoadingOrb className="architecture-stream-orb" state={orbState} />
      ) : null}
      <div className="architecture-events-shell" ref={shellRef} onScroll={handleScroll}>
        <div className="architecture-events" data-architecture-events id={contentId} ref={eventsRef} aria-live="polite">
          {rows.map((row, index) => row.kind === "activity" ? (
            <AgentActivityRow
              expanded={openDetailKeys.has(row.key)}
              key={row.key}
              onToggleDetail={() => toggleDetail(row.key)}
              row={row}
            />
          ) : (
            <AgentActivityTextRow
              active={Boolean(!transcript && row.active && index === 0)}
              key={row.key}
              row={row}
            />
          ))}
        </div>
      </div>
      {collapsed ? (
        <div className="prompt-expand-fade architecture-stream-expand-fade" aria-hidden="true"></div>
      ) : null}
      {collapsed ? (
        <button
          type="button"
          className={`prompt-expand-button architecture-stream-expand-button ${generating ? "is-generating" : ""}`.trim()}
          aria-controls={contentId}
          aria-expanded={expanded}
          aria-label={generating ? "Expand generation log" : undefined}
          onClick={() => setExpanded(true)}
        >
          {generating && orbState ? <LoadingOrb state={orbState} /> : null}
          <span className="prompt-expand-button-label">
            {generating ? "Generating" : "Expand"}
          </span>
        </button>
      ) : null}
    </div>
  )
}

function AgentActivityRow({ expanded, onToggleDetail, row }) {
  const activity = row.activity
  const Icon = activityIcon(activity)
  const expandable = activityHasDetail(activity)
  const statusClass = activity.status === "failed" ? "is-error" : activity.status === "running" ? "is-running" : "is-done"
  const summary = (
    <>
      <Icon className="agent-activity-icon" aria-hidden="true" />
      <span className="agent-activity-label">
        <FormattedStreamText text={activity.label} />
      </span>
    </>
  )

  return (
    <div className={`agent-activity-row is-activity ${statusClass} ${expanded ? "is-detail-open" : ""}`.trim()}>
      <AgentActivitySource row={row} />
      {expandable ? (
        <button
          type="button"
          className="agent-activity-summary agent-activity-toggle"
          aria-expanded={expanded}
          onClick={onToggleDetail}
        >
          {summary}
          <ChevronRight className="agent-activity-chevron" aria-hidden="true" />
        </button>
      ) : (
        <div className="agent-activity-summary">{summary}</div>
      )}
      {expandable && expanded ? <AgentActivityDetail activity={activity} /> : null}
    </div>
  )
}

function AgentActivityDetail({ activity }) {
  return (
    <div className="agent-activity-detail">
      {activity.command ? (
        <pre className="agent-activity-detail-command">{activity.command}</pre>
      ) : null}
      {activity.changes.length > 0 ? (
        <ul className="agent-activity-detail-changes">
          {activity.changes.map((change, index) => (
            <li key={`${change.path}-${index}`}>
              <span className={`agent-activity-change-kind is-${change.kind}`}>{changeKindLabel(change.kind)}</span>
              <code>{change.path}</code>
            </li>
          ))}
        </ul>
      ) : null}
      {activity.output ? (
        <pre className="agent-activity-detail-output">{activity.output}</pre>
      ) : null}
      {activity.exitCode !== null ? (
        <div className="agent-activity-detail-exit">Exit code: {activity.exitCode}</div>
      ) : null}
    </div>
  )
}

function AgentActivityTextRow({ active, row }) {
  return (
    <div className={`agent-activity-row is-text is-${row.tone} ${active ? "is-active" : ""}`.trim()}>
      <span className="agent-activity-text" data-progress-text={progressTextAttribute(row.text)}>
        <AgentActivitySource row={row} />
        <FormattedStreamText text={row.text} />
      </span>
      {row.count > 1 ? (
        <span className="agent-activity-repeat" aria-label={`Repeated ${row.count} times`}>×{row.count}</span>
      ) : null}
    </div>
  )
}

function AgentActivitySource({ row }) {
  if (!row.agentRunId) return null

  return (
    <span className="agent-activity-source">
      {row.commitSha ? `Commit ${row.commitSha.slice(0, 7)} · ` : ""}Run #{row.agentRunId}
    </span>
  )
}

function progressTextAttribute(text) {
  return String(text || "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^[-*]\s+/gm, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1")
}

function FormattedStreamText({ text }) {
  const blocks = markdownBlocks(text)

  if (blocks.length === 1) return blocks[0]

  return blocks.map((block, index) => (
    <span className="architecture-markdown-block" key={index}>{block}</span>
  ))
}

function markdownBlocks(text) {
  return String(text || "")
    .split(/\n+/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map(markdownBlock)
}

function markdownBlock(text) {
  const heading = text.match(/^#{1,6}\s+(.+)$/)
  if (heading) return <strong className="architecture-markdown-heading">{inlineMarkdown(heading[1])}</strong>

  const bullet = text.match(/^[-*]\s+(.+)$/)
  if (bullet) {
    return (
      <>
        <span className="architecture-markdown-bullet" aria-hidden="true">•</span>
        {inlineMarkdown(bullet[1])}
      </>
    )
  }

  return <>{inlineMarkdown(text)}</>
}

function inlineMarkdown(text) {
  const pattern = /(`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))/g
  const nodes = []
  let cursor = 0
  let match

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index))

    if (match[2]) {
      nodes.push(<code key={nodes.length}>{match[2]}</code>)
    } else if (match[3] || match[4]) {
      nodes.push(<strong key={nodes.length}>{match[3] || match[4]}</strong>)
    } else if (match[5] && match[6]) {
      nodes.push(
        <a href={match[6]} key={nodes.length} rel="noreferrer" target="_blank">
          {match[5]}
        </a>
      )
    }

    cursor = pattern.lastIndex
  }

  if (cursor < text.length) nodes.push(text.slice(cursor))

  return nodes
}

export function formatEventText(text, { compact = true } = {}) {
  const formatted = String(text || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .replace(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z\s+(?:WARN|INFO|DEBUG|ERROR)\s+/i, "")
    .trim()
  const compacted = formatted.replace(/\s+/g, " ")

  if (compacted.match(/^(assistant|system)$/i)) return ""
  if (compacted.match(/^(?:Conceptive|vibecheck) is still thinking$/i)) return ""
  if (compacted.match(/^(?:Conceptive|vibecheck) is thinking$/i)) return ""

  if (!compact) return formatted

  if (compacted.match(/^codex\b/i)) return "Running architecture analysis."
  if (streamTextLooksLikeCodeDump(compacted)) return "Reviewing generated implementation details."
  if (compacted.endsWith("...") && compacted.length <= STREAM_TEXT_MAX_LENGTH + 3) return compacted
  if (compacted.length > STREAM_TEXT_MAX_LENGTH) return `${compacted.slice(0, STREAM_TEXT_MAX_LENGTH).trim()}...`

  return compacted
}

function streamTextLooksLikeCodeDump(text) {
  if (text.length < STREAM_TEXT_MAX_LENGTH) return false

  const matches = text.match(new RegExp(STREAM_CODE_DUMP_HINT.source, "gi")) || []
  return matches.length >= 6
}
